import { useCallback, useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Send } from "lucide-react";

const MESSAGE_POLLING_INTERVAL_MS = 4000;

export function MessagesList() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    api.get("/messages/threads").then((r) => setThreads(r.data));
  }, [user, loading, navigate]);

  if (!user) return null;
  return (
    <div className="min-h-screen bg-[#F4F2EC]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="overline">Messages</div>
        <h1 className="font-serif text-4xl md:text-5xl mt-1 mb-8">Conversations</h1>
        {threads.length === 0 ? (
          <div className="bg-white border border-subtle rounded-2xl p-10 text-center text-muted2" data-testid="no-threads">
            No conversations yet. Start one from an experience page.
          </div>
        ) : (
          <div className="space-y-3">
            {threads.map((t) => {
              const other = user.user_id === t.traveler_id ? t.host_name : t.traveler_name;
              return (
                <Link
                  key={t.id}
                  data-testid={`thread-${t.id}`}
                  to={`/messages/${t.id}`}
                  className="block bg-white border border-subtle rounded-2xl p-5 hover:border-moss transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-serif text-xl">{other}</div>
                      <div className="text-xs text-muted2">Re: {t.experience_title}</div>
                    </div>
                  </div>
                  {t.last_message && (
                    <p className="text-sm text-muted2 mt-2 truncate">
                      <span className="font-medium">{t.last_message.sender_name}:</span> {t.last_message.body}
                    </p>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function MessageThread() {
  const { threadId } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/thread/${threadId}`);
      setThread(data);
    } catch (err) {
      console.error("Load thread failed", err);
      navigate("/messages");
    }
  }, [threadId, navigate]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    load();
    const iv = setInterval(load, MESSAGE_POLLING_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [threadId, user, loading, navigate, load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/messages/thread/${threadId}/send`, { body });
      setBody("");
      load();
    } finally { setSending(false); }
  };

  if (!user || !thread) return null;
  const other = user.user_id === thread.traveler_id ? thread.host_name : thread.traveler_name;

  return (
    <div className="min-h-screen bg-[#F4F2EC] flex flex-col">
      <Navbar />
      <div className="max-w-3xl w-full mx-auto px-6 py-6 flex-1 flex flex-col">
        <button data-testid="back-to-threads" onClick={() => navigate("/messages")} className="inline-flex items-center gap-1 text-sm text-muted2 hover:text-moss mb-4">
          <ArrowLeft className="h-4 w-4" /> All conversations
        </button>
        <div className="bg-white border border-subtle rounded-2xl p-6 mb-4">
          <div className="font-serif text-2xl">{other}</div>
          <Link to={`/experience/${thread.experience_id}`} className="text-xs text-moss hover:underline">Re: {thread.experience_title}</Link>
        </div>
        <div className="flex-1 bg-white border border-subtle rounded-2xl p-6 mb-4 min-h-[360px] overflow-y-auto space-y-3" data-testid="messages-list">
          {(thread.messages || []).map((m) => {
            const mine = m.sender_id === user.user_id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${mine ? "bg-moss text-canvas" : "bg-subtle text-ink"}`}>
                  <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                  <div className={`text-[10px] mt-1 ${mine ? "text-canvas/70" : "text-muted2"}`}>
                    {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="flex gap-2">
          <Input
            data-testid="message-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write a message…"
            className="rounded-full bg-white border-subtle"
          />
          <Button data-testid="send-message" type="submit" disabled={sending} className="rounded-full bg-moss hover:bg-moss-hover text-canvas px-5">
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
